import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

interface DonorImpactReportProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  donor: any;
  contributions: any[];
  fundedDeals: any[];
}

export function DonorImpactReport({ open, onOpenChange, donor, contributions, fundedDeals }: DonorImpactReportProps) {
  const [dateRange, setDateRange] = useState("all_time");
  const [isGenerating, setIsGenerating] = useState(false);

  const generatePDF = async () => {
    setIsGenerating(true);
    try {
      const doc = new jsPDF();
      
      // Header
      doc.setFontSize(20);
      doc.text(`Thank You ${donor.name}!`, 20, 20);
      
      doc.setFontSize(12);
      doc.text("Impact Report", 20, 30);
      
      // Summary Stats
      doc.setFontSize(14);
      doc.text("Your Impact at a Glance", 20, 45);
      
      const totalContributed = contributions.reduce((sum, c) => sum + c.amount, 0);
      const athletesFunded = new Set(fundedDeals.map(d => d.athlete_id)).size;
      
      doc.setFontSize(11);
      doc.text(`Total Support: $${totalContributed.toLocaleString()}`, 20, 55);
      doc.text(`Athletes Funded: ${athletesFunded}`, 20, 62);
      doc.text(`Deals Supported: ${fundedDeals.length}`, 20, 69);
      
      // Funded Athletes Table
      if (fundedDeals.length > 0) {
        doc.setFontSize(14);
        doc.text("Athletes You've Supported", 20, 85);
        
        const tableData = fundedDeals.map(deal => [
          deal.athletes?.name || "—",
          deal.athletes?.sport || "—",
          deal.athletes?.year || "—",
          deal.title,
          `$${deal.amount.toLocaleString()}`,
        ]);
        
        autoTable(doc, {
          startY: 90,
          head: [["Athlete", "Sport", "Year", "Deal", "Value"]],
          body: tableData,
          theme: "grid",
          headStyles: { fillColor: [59, 130, 246] },
        });
      }
      
      // Contribution History
      const finalY = (doc as any).lastAutoTable?.finalY || 95;
      doc.setFontSize(14);
      doc.text("Your Contribution History", 20, finalY + 15);
      
      const contributionData = contributions.map(c => [
        new Date(c.contribution_date).toLocaleDateString(),
        `$${c.amount.toLocaleString()}`,
        c.payment_method,
        c.is_tax_deductible ? "Yes" : "No",
      ]);
      
      autoTable(doc, {
        startY: finalY + 20,
        head: [["Date", "Amount", "Method", "Tax Deductible"]],
        body: contributionData,
        theme: "grid",
        headStyles: { fillColor: [59, 130, 246] },
      });
      
      // Footer
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.text(
          `Generated ${new Date().toLocaleDateString()}`,
          20,
          doc.internal.pageSize.height - 10
        );
      }
      
      // Save
      doc.save(`impact-report-${donor.name.replace(/\s/g, '-')}.pdf`);
      toast.success("Impact report generated successfully");
    } catch (error: any) {
      toast.error("Failed to generate report");
      console.error(error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generate Impact Report</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">Date Range</label>
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_time">All Time</SelectItem>
                <SelectItem value="this_year">This Year</SelectItem>
                <SelectItem value="last_quarter">Last Quarter</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="bg-muted p-4 rounded-lg space-y-2">
            <p className="text-sm font-medium">Report will include:</p>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>Total contributions and statistics</li>
              <li>List of funded athletes and deals</li>
              <li>Contribution history</li>
              <li>Collective impact metrics</li>
            </ul>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={generatePDF} disabled={isGenerating}>
              {isGenerating ? "Generating..." : "Generate PDF"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
